import React, { lazy } from "react";
import { Outlet, Route } from "react-router-dom";
import { AuthenticationGuard } from "@/components/AuthenticationGuard/AuthenticationGuard";

const GrowthStagesPrototype = lazy(() =>
  import("@/modules/growthStages/GrowthStagesPrototype").then((m) => ({
    default: m.GrowthStagesPrototype,
  })),
);

const GrowthStagesStacked = lazy(() =>
  import(
    "@/modules/growthStages/features/GrowthStagesStacked/GrowthStagesStacked"
  ).then((m) => ({
    default: m.GrowthStagesStacked,
  })),
);

const GrowthStagesStackedDark = lazy(() =>
  import(
    "@/modules/growthStages/features/GrowthStagesStacked/GrowthStagesStackedDark"
  ).then((m) => ({
    default: m.GrowthStagesStackedDark,
  })),
);

const ProcessWheelDark = lazy(() =>
  import("@/modules/growthStages/features/ProcessWheel/ProcessWheelDark").then(
    (m) => ({
      default: m.ProcessWheelDark,
    }),
  ),
);

const ResourceLibraryWheel = lazy(() =>
  import(
    "@/modules/growthStages/features/ResourceLibraryWheel/ResourceLibraryWheel"
  ).then((m) => ({
    default: m.ResourceLibraryWheel,
  })),
);

const CoachPortal = lazy(() =>
  import("@/modules/growthStages/features/CoachPortal/CoachPortal").then(
    (m) => ({
      default: m.CoachPortal,
    }),
  ),
);

const LearningPathsMock = lazy(() =>
  import(
    "@/modules/growthStages/features/LearningPathsMock/LearningPathsMock"
  ).then((m) => ({
    default: m.LearningPathsMock,
  })),
);

const PrototypeIndex = lazy(() =>
  import("@/modules/growthStages/features/PrototypeIndex/PrototypeIndex").then(
    (m) => ({
      default: m.PrototypeIndex,
    }),
  ),
);

const ServiceAcademy = lazy(() =>
  import("@/modules/growthStages/features/ServiceAcademy/ServiceAcademy").then(
    (m) => ({
      default: m.ServiceAcademy,
    }),
  ),
);

const ServiceFeeAcademy = lazy(() =>
  import(
    "@/modules/growthStages/features/ServiceFeeAcademy/ServiceFeeAcademy"
  ).then((m) => ({
    default: m.ServiceFeeAcademy,
  })),
);

const ExecutivePreview = lazy(() =>
  import(
    "@/modules/growthStages/features/ExecutivePreview/ExecutivePreview"
  ).then((m) => ({
    default: m.ExecutivePreview,
  })),
);

const CreateLessonMockup = lazy(() =>
  import(
    "@/modules/growthStages/features/CreateLessonMockup/CreateLessonMockup"
  ).then((m) => ({
    default: m.CreateLessonMockup,
  })),
);

const WikiHomePage = lazy(() =>
  import("@/modules/growthStages/wiki/pages/WikiHomePage").then((m) => ({
    default: m.WikiHomePage,
  })),
);

const WikiIndexPage = lazy(() =>
  import("@/modules/growthStages/wiki/pages/WikiIndexPage").then((m) => ({
    default: m.WikiIndexPage,
  })),
);

const WikiPageDetail = lazy(() =>
  import("@/modules/growthStages/wiki/pages/WikiPageDetail").then((m) => ({
    default: m.WikiPageDetail,
  })),
);

const WikiAskPage = lazy(() =>
  import("@/modules/growthStages/wiki/pages/WikiAskPage").then((m) => ({
    default: m.WikiAskPage,
  })),
);

const WikiLogPage = lazy(() =>
  import("@/modules/growthStages/wiki/pages/WikiLogPage").then((m) => ({
    default: m.WikiLogPage,
  })),
);

const ProductDirectory = lazy(() =>
  import(
    "@/modules/growthStages/features/ProductDirectory/ProductDirectory"
  ).then((m) => ({
    default: m.ProductDirectory,
  })),
);

const ProductDetail = lazy(() =>
  import("@/modules/growthStages/features/ProductDirectory/ProductDetail").then(
    (m) => ({
      default: m.ProductDetail,
    }),
  ),
);

const ProductDirectoryBento = lazy(() =>
  import(
    "@/modules/growthStages/features/ProductDirectory/ProductDirectoryBento"
  ).then((m) => ({
    default: m.ProductDirectoryBento,
  })),
);

const ProductDirectoryV3 = lazy(() =>
  import(
    "@/modules/growthStages/features/ProductDirectory/ProductDirectoryV3"
  ).then((m) => ({
    default: m.ProductDirectoryV3,
  })),
);

const ProductMatrix = lazy(() =>
  import("@/modules/growthStages/features/ProductMatrix/ProductMatrix").then(
    (m) => ({
      default: m.ProductMatrix,
    }),
  ),
);

const CoachingDashboard = lazy(() =>
  import(
    "@/modules/growthStages/features/CoachingDashboard/CoachingDashboard"
  ).then((m) => ({
    default: m.CoachingDashboard,
  })),
);

const CompassLanding = lazy(() =>
  import("@/modules/growthStages/features/CompassLanding/CompassLanding").then(
    (m) => ({
      default: m.CompassLanding,
    }),
  ),
);

const CompassDashboard = lazy(() =>
  import(
    "@/modules/growthStages/features/CompassLanding/CompassDashboard"
  ).then((m) => ({
    default: m.CompassDashboard,
  })),
);

const StageDiagnostic = lazy(() =>
  import(
    "@/modules/growthStages/features/StageDiagnostic/StageDiagnostic"
  ).then((m) => ({
    default: m.StageDiagnostic,
  })),
);

const StrategyNote = lazy(() =>
  import("@/modules/growthStages/features/StrategyNote/StrategyNote").then(
    (m) => ({
      default: m.StrategyNote,
    }),
  ),
);

const TopNavMockup = lazy(() =>
  import("@/modules/prototypes/TopNavMockup/TopNavMockup").then((m) => ({
    default: m.TopNavMockup,
  })),
);

/**
 * Pass-through layout for the prototypes mount — AuthenticationGuard takes a
 * component, and the nested prototype routes render through its Outlet.
 */
const PrototypesOutlet = () => <Outlet />;

/**
 * GrowthStagesRoutes
 *
 * Prototype routes for the Growth Stages feature.
 * Authenticated users only (M6 route hygiene) — the design prototypes stay
 * reachable for logged-in users but are no longer public.
 */
const GrowthStagesRoutes = (
  <Route
    element={<AuthenticationGuard component={PrototypesOutlet} />}
    path="/prototypes"
  >
    <Route element={<PrototypeIndex />} path="index" />
    <Route element={<GrowthStagesPrototype />} path="member-hub/experience" />
    <Route element={<GrowthStagesStacked />} path="member-hub/stages-stacked" />
    <Route
      element={<GrowthStagesStackedDark />}
      path="member-hub/stages-stacked-dark"
    />
    <Route element={<ProcessWheelDark />} path="strategy/process-wheel" />
    <Route
      element={<ResourceLibraryWheel />}
      path="member-hub/resource-wheel"
    />
    <Route element={<WikiHomePage />} path="member-hub/wiki" />
    <Route element={<WikiIndexPage />} path="member-hub/wiki/index" />
    <Route element={<WikiPageDetail />} path="member-hub/wiki/pages/:slug" />
    <Route element={<WikiAskPage />} path="member-hub/wiki/ask" />
    <Route element={<WikiLogPage />} path="member-hub/wiki/log" />
    <Route element={<ProductDirectory />} path="member-hub/products" />
    <Route
      element={<ProductDirectoryBento />}
      path="member-hub/products-bento"
    />
    <Route element={<ProductDirectoryV3 />} path="member-hub/products-v3" />
    <Route element={<ProductMatrix />} path="member-hub/product-matrix" />
    <Route element={<ProductDetail />} path="member-hub/products/:slug" />
    <Route element={<CoachPortal />} path="coach-tools/portal" />
    <Route element={<CoachingDashboard />} path="coach-tools/dashboard" />
    <Route element={<StageDiagnostic />} path="coach-tools/stage-diagnostic" />
    <Route element={<LearningPathsMock />} path="training/paths" />
    <Route element={<ServiceAcademy />} path="training/academy" />
    <Route element={<ServiceFeeAcademy />} path="training/service-fee" />
    <Route element={<CreateLessonMockup />} path="admin/create-lesson" />
    <Route element={<StrategyNote />} path="strategy/strategy-note" />
    <Route element={<ExecutivePreview />} path="marketing/executive" />
    <Route element={<CompassLanding />} path="coach-tools/compass" />
    <Route
      element={<CompassDashboard />}
      path="coach-tools/compass-dashboard"
    />
    <Route element={<TopNavMockup />} path="navigation/top-nav" />
    {/* Legacy URL — Compass lived under marketing before moving to coach-tools */}
    <Route element={<CompassLanding />} path="marketing/compass" />
  </Route>
);

export default GrowthStagesRoutes;
